import React from 'react';
// eslint-disable-next-line no-restricted-imports
import { useBillingStatus } from '../../hooks/useBillingStatus';

interface QuotaDisplayProps {
  variant: 'header' | 'page' | 'compact';
  showUpgradePrompt?: boolean;
  onPurchaseCredits?: () => void;
}

const QuotaDisplay: React.FC<QuotaDisplayProps> = ({ 
  variant, 
  showUpgradePrompt = false,
  onPurchaseCredits 
}) => {
  const { billingStatus, isLoading, error } = useBillingStatus();

  if (isLoading) {
    return (
      <div className={getContainerClasses(variant)}>
        <div className="animate-pulse">
          {variant === 'header' ? (
            <div className="flex items-center space-x-2">
              <div className="h-4 bg-gray-300 rounded w-20"></div>
              <div className="h-4 bg-gray-300 rounded w-16"></div>
            </div>
          ) : (
            <>
              <div className="h-4 bg-gray-300 rounded w-24 mb-2"></div>
              <div className="h-2 bg-gray-300 rounded w-full"></div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (error || !billingStatus) {
    return (
      <div className={getContainerClasses(variant)}>
        <span className="text-red-500 text-sm">Error loading quota</span>
      </div>
    );
  }

  const { exportQuotaRemaining, planType } = billingStatus;
  const isLowQuota = exportQuotaRemaining < 50;
  const planBadgeText = getPlanBadgeText(planType);

  // Header variant - minimal display for navbar
  if (variant === 'header') {
    return (
      <div className="flex items-center space-x-3">
        {planBadgeText && (
          <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
            {planBadgeText}
          </span>
        )}
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Credits:</span>
          <span className={`font-semibold ${isLowQuota ? 'text-orange-600' : 'text-green-600'}`}>
            {exportQuotaRemaining}
          </span>
          {isLowQuota && onPurchaseCredits && (
            <button
              onClick={onPurchaseCredits}
              className="text-xs bg-orange-500 text-white px-2 py-1 rounded hover:bg-orange-600"
            >
              Buy More
            </button>
          )}
        </div>
      </div>
    );
  }

  // Compact variant - for cards or sidebar
  if (variant === 'compact') {
    return (
      <div className="bg-white border rounded-lg p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Export Quota</span>
          {planBadgeText && (
            <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
              {planBadgeText}
            </span>
          )}
        </div>
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-lg font-bold text-gray-900">{exportQuotaRemaining}</span>
            <span className="text-sm text-gray-500">credits</span>
          </div>
          <QuotaProgressBar current={exportQuotaRemaining} max={1000} isLow={isLowQuota} />
        </div>
        {(isLowQuota || showUpgradePrompt) && onPurchaseCredits && (
          <button
            onClick={onPurchaseCredits}
            className="w-full text-sm bg-purple-600 text-white py-2 rounded hover:bg-purple-700"
          >
            Purchase More Credits
          </button>
        )}
      </div>
    );
  }

  // Page variant - detailed display for main content areas
  return (
    <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Export Credits</h3>
          {planBadgeText && (
            <span className="inline-block mt-1 text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
              {planBadgeText} Plan
            </span>
          )}
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-purple-600">{exportQuotaRemaining}</div>
          <div className="text-sm text-gray-600">credits remaining</div>
        </div>
      </div>

      <div className="mb-4">
        <QuotaProgressBar current={exportQuotaRemaining} max={1000} isLow={isLowQuota} />
      </div>

      {isLowQuota && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
            <span className="text-orange-800 font-medium">Low quota warning</span>
          </div>
          <p className="text-orange-700 text-sm mt-1">
            You're running low on export credits. Purchase more to continue exporting leads.
          </p>
        </div>
      )}

      <div className="flex space-x-3">
        {onPurchaseCredits && (
          <button
            onClick={onPurchaseCredits}
            className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 font-medium"
          >
            Purchase More Credits
          </button>
        )}
        <button className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 font-medium">
          View Usage History
        </button>
      </div>
    </div>
  );
};

// Helper component for progress bar
interface QuotaProgressBarProps {
  current: number;
  max: number;
  isLow: boolean;
}

const QuotaProgressBar: React.FC<QuotaProgressBarProps> = ({ current, max, isLow }) => {
  const percentage = Math.min((current / max) * 100, 100);
  const barColor = isLow ? 'bg-orange-500' : 'bg-green-500';

  return (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className={`h-2 rounded-full transition-all duration-300 ${barColor}`}
        style={{ width: `${percentage}%` }}
      ></div>
    </div>
  );
};

// Helper functions
const getContainerClasses = (variant: 'header' | 'page' | 'compact'): string => {
  switch (variant) {
    case 'header':
      return 'flex items-center';
    case 'compact':
      return 'w-full';
    case 'page':
      return 'w-full';
    default:
      return '';
  }
};

const getPlanBadgeText = (planType: 'junior_sdr' | 'top_qualifier_sdr' | null): string | null => {
  switch (planType) {
    case 'junior_sdr':
      return 'Junior SDR';
    case 'top_qualifier_sdr':
      return 'Top Qualifier';
    default:
      return null;
  }
};

export default QuotaDisplay;